import React from 'react';
import {
  VStack, HStack, Center, Text, Card, Heading, ScrollView, Box,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { useEvaluationStream } from '@/common/hooks/useEvaluationStream';
import { useTaskSubmissionActions, useTaskSubmissionStore } from '@/student/task/stores/TaskSubmissionStore';
import EditorShell from '@/student/task/composer/components/EditorShell';
import EditorToolbar from '@/student/task/composer/components/EditorToolbar';
import ResourceSidebar from '@/student/task/ResourceSidebar';
import TaskDescription from '@/student/task/components/Instructions/TaskDescription';
import TaskObjectives from '@/student/task/components/Instructions/TaskObjectives';
import AssistanceDrawer from '@/student/task/components/AssistanceDrawer';
import MCQPreviewPanel from '@/student/module/components/task-types/event/components/MCQPreviewPanel';
import OralExamPrepWebLayout from '@/student/task/screenLayouts/OralExamPrepWebLayout';
import { isMeetingOnlyTask } from '@/common/utils/tasks/isEventDeliveredTask';
import { isOralExamTask } from '@/common/utils/tasks/isOralExamTask';
import { usePreviewContent, usePreviewNavigation } from '../hooks/useModulePreviewStore';
import useModulePreviewData from '../hooks/useModulePreviewData';

// Preview opts into a hard timeout because authors may iterate quickly and
// stuck streams should fail fast rather than block the preview UI.
const PREVIEW_STREAM_TIMEOUT_MS = 180_000;

const TaskPreview: React.FC = () => {
  const { t } = useTranslation();
  const { setActiveView } = usePreviewNavigation();
  const { module: rawModule, tasks: rawTasks, selectedTask: rawSelectedTask } = usePreviewContent();
  const previewData = useModulePreviewData(rawModule, rawTasks);
  const { module, tasks } = previewData;
  const selectedTask = tasks?.find((task) => task.id === rawSelectedTask?.id);

  const [previewDraftHtml, setPreviewDraftHtml] = React.useState('');

  const { resetEvaluation } = useTaskSubmissionActions();

  const moduleVersion = rawModule?.version ?? module?.version ?? '0.0';
  const meetingOnlyTask = isMeetingOnlyTask(selectedTask);
  const isMcqTask = selectedTask?.type === 'multiple_choice';
  const isActivityTask = selectedTask?.type === 'activity';
  const showEssayComposer = !meetingOnlyTask && !isMcqTask && !isActivityTask;
  const hasAiTutor = showEssayComposer && selectedTask?.evaluation?.mode === 'auto';
  const draftIsEmpty = previewDraftHtml.trim().length === 0;

  const streamUrl = module && selectedTask
    ? `/modules/${module.id}/versions/${moduleVersion}/tasks/${selectedTask.id}/ai-tutor/preview-formative-feedback/stream`
    : '';

  const { isStreaming, startEvaluation, stopEvaluation } = useEvaluationStream({
    timeoutMs: PREVIEW_STREAM_TIMEOUT_MS,
  });
  const isEvaluating = useTaskSubmissionStore((s) => s.evaluation.isEvaluating);

  // Each preview task is a clean slate: clear local draft + any prior store
  // evaluation/feedback so the singleton TaskSubmissionStore does not leak
  // state between tasks the author switches between.
  React.useEffect(() => {
    stopEvaluation();
    setPreviewDraftHtml('');
    resetEvaluation();
  }, [selectedTask?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = React.useCallback(() => {
    stopEvaluation();
  }, [stopEvaluation]);

  const handleEvaluate = React.useCallback(() => {
    const trimmed = previewDraftHtml.trim();
    if (!trimmed || !selectedTask || isEvaluating || isStreaming) return;
    resetEvaluation();
    startEvaluation(streamUrl, { draftHtml: trimmed });
  }, [
    previewDraftHtml,
    selectedTask,
    isEvaluating,
    isStreaming,
    streamUrl,
    startEvaluation,
    resetEvaluation,
  ]);

  if (!module || !selectedTask) {
    return (
      <Center flex={1}>
        <Text>No task selected for preview</Text>
      </Center>
    );
  }

  const resources = selectedTask.resources || [];
  const thumbnails: Record<string, string | undefined> = {};
  const taskScope = { courseId: 'preview', moduleId: module.id, taskId: selectedTask.id };

  if (isOralExamTask(selectedTask)) {
    return (
      <OralExamPrepWebLayout
        task={selectedTask}
        resources={resources}
        module={module}
        tasks={tasks ?? []}
        courseId="preview"
        moduleId={module.id}
        onBack={() => setActiveView('module')}
      />
    );
  }

  return (
    <HStack bg="white" flex={1}>
      <VStack flex={1.5} space="md" pl="$6" pr="$4" pt="$6" backgroundColor="$backgroundLight100">
        <Heading size="lg" testID="task-title">
          {selectedTask.title}
        </Heading>
        <ScrollView flex={1} pr="$2" pb="$4">
          <Card>
            <Text size="xs" mb="$2" color="$textSubtle">YOUR TASK</Text>
            <TaskDescription description={selectedTask?.description} />
            <TaskObjectives objectives={selectedTask?.analysis?.dimensions} />
          </Card>
          <Card mt="$6">
            <Text size="xs" mb="$2" color="$textSubtle">RESOURCES</Text>
            <ResourceSidebar testID="resource-sidebar" resources={resources} thumbnails={thumbnails} />
          </Card>
        </ScrollView>
      </VStack>

      <VStack flex={2} mt="$4" px="$6" pb="$6">
        {meetingOnlyTask ? null : isMcqTask ? (
          selectedTask.mcqConfig?.questions?.length ? (
            <MCQPreviewPanel mcqConfig={selectedTask.mcqConfig} />
          ) : null
        ) : isActivityTask ? (
          <Box flex={1} justifyContent="center">
            <Text>{t('student.task.composer.messages.activity-composer.message')}</Text>
          </Box>
        ) : (
          <Box flex={1} position="relative" testID="preview-response-composer">
            {hasAiTutor && (
              <EditorToolbar
                showEvaluate
                disableButtons={draftIsEmpty || isEvaluating || isStreaming}
                onEvaluate={handleEvaluate}
              />
            )}
            <EditorShell
              key={selectedTask.id}
              testID="preview-response-composer"
              initialHtml={previewDraftHtml}
              onHtmlChange={(html) => setPreviewDraftHtml(html)}
              readonly={false}
              borderless
              showHistoryControl={false}
            />
          </Box>
        )}
      </VStack>

      {hasAiTutor && (
        <VStack flex={1} borderLeftWidth={1} borderColor="$borderLight">
          <AssistanceDrawer
            task={selectedTask}
            taskScope={taskScope}
            readonly
            hideStudentFeedbackControls
            cancelEvaluationAction={handleCancel}
            retryEvaluationAction={handleEvaluate}
          />
        </VStack>
      )}
    </HStack>
  );
};

export default TaskPreview;
